import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, ChevronDown, CreditCard, Tag, User as UserIcon, BookOpen, MessageCircle, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useViewAs } from "@/context/ViewAsContext";
import LicenseDetailsModal from "@/components/LicenseDetailsModal";
import { BRAND } from "@/config/brand";

// Sprint M5.36 (29.07.2026) — Issue 9: Nav auf Tertiär-Ebene reduziert
// (Sidebar/Cockpit-Quicknav übernimmt Primär- und Sekundär-Navigation).
// Für eingeloggte Dashboard-User bleibt nur „Mein Cockpit" als schnelle Rückkehr.
const NAV_LINKS_AUTHED = [
  { to: "/cockpit", label: "Mein PocketCoach", testid: "nav-dashboard", end: true },
];

// Sprint 7 — "landit Pro Solo"-Label entfernt, ist nun einfach "Pro Solo"
// (rebrand-agnostic, da BRAND.name oben links sowieso sichtbar).
// Sprint M5.47 Hotfix (02.08.2026) — Pionier bekommt eigenes Header-Pin
// „Vorab-Einblick aktiv" (amber), KEIN „Pro Solo starten"-CTA. Pionier
// ist kein Upgrade-Ziel — er ist bereits im Vorab-Einblick-Programm.
function deriveHeaderPin(licenseTier, licenseStatus, statusLabel, statusColor, isPionier = false) {
  if (isPionier) {
    return {
      label: "Pionier aktiv",
      bg: "#F59E0B",
      color: "#FFFFFF",
      href: "/cockpit",
      testId: "dashboard-header-pin-pionier",
    };
  }
  if (licenseTier === "free") {
    return {
      label: "Pro Solo starten",
      bg: "var(--fts-amber-free)",
      color: "#1E293B",
      href: "/dashboard/abo",
      testId: "dashboard-header-pin-free",
    };
  }
  if (licenseTier === "pro_studio" && licenseStatus !== "expired" && licenseStatus !== "cancelled") {
    return {
      label: "Pro Studio aktiv",
      bg: "#4563BB",
      color: "#FFFFFF",
      href: "/dashboard",
      testId: "dashboard-header-pin-studio",
    };
  }
  // Vorlaunch-Sprint 04.08.2026 — Gate B: Plan-Badge differenziert Monat vs. Jahr.
  // Verbindliche Labels laut Chief-of-Staff-Entscheidung:
  //   pro_solo_monthly → "Pro Solo Monat"
  //   pro_solo_annual  → "Pro Solo Jahr"
  // Keine neuen Lizenztypen; nur die bereits vorhandenen Werte separat darstellen.
  if (
    licenseTier === "pro_solo_monthly" &&
    licenseStatus !== "expired" &&
    licenseStatus !== "cancelled"
  ) {
    return {
      label: "Pro Solo Monat",
      bg: "var(--fts-success)",
      color: "#FFFFFF",
      href: "/dashboard",
      testId: "dashboard-header-pin-pro-monthly",
    };
  }
  if (
    licenseTier === "pro_solo_annual" &&
    licenseStatus !== "expired" &&
    licenseStatus !== "cancelled"
  ) {
    return {
      label: "Pro Solo Jahr",
      bg: "var(--fts-success)",
      color: "#FFFFFF",
      href: "/dashboard",
      testId: "dashboard-header-pin-pro-annual",
    };
  }
  // Trial / cancelled / expired — bestehende Logik via statusLabel/statusColor.
  const fallback = {
    warning: { bg: "var(--fts-amber-free)", color: "#1E293B" },
    success: { bg: "var(--fts-success)", color: "#FFFFFF" },
    "primary-bright": { bg: "var(--fts-primary-bright)", color: "#FFFFFF" },
    muted: { bg: "#94A3B8", color: "#FFFFFF" },
  }[statusColor] || { bg: "var(--fts-amber-free)", color: "#1E293B" };
  return {
    label: statusLabel || "Pro Solo starten",
    bg: fallback.bg,
    color: fallback.color,
    href: "/dashboard/abo",
    testId: "dashboard-header-pin-other",
  };
}

export default function DashboardHeader({
  firstName,
  statusLabel,
  statusColor,
  licenseTier,
  licenseStatus,
  licenseData,
}) {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { override, setOverride, isDevMode } = useViewAs();
  const pin = deriveHeaderPin(licenseTier, licenseStatus, statusLabel, statusColor, !!user?.is_pionier);
  // v2.10.1 — Modal-State für License-Details. Free-Tier öffnet kein Modal,
  // navigiert weiter zu /dashboard/abo wie bisher (Upgrade-CTA-Flow).
  const [modalOpen, setModalOpen] = useState(false);
  // Sprint M5.47 Hotfix (02.08.2026): Pioniere haben kein Pro-Abo, deshalb
  // navigiert der Badge-Klick zurück ins Cockpit statt ins License-Modal.
  const supportsModal = licenseTier && licenseTier !== "free" && !user?.is_pionier;
  // Sprint M5.40 (30.07.2026) — Block A1/B5: Free hat weder „Meine Flexpfade" noch „Mein Plan".
  const isProLike = supportsModal && licenseStatus !== "expired" && licenseStatus !== "cancelled";

  const handleBadgeClick = () => {
    if (supportsModal) {
      setModalOpen(true);
    } else {
      navigate(pin.href);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-100"
      data-testid="app-header"
      data-component="dashboard-header"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        {/* Sprint M5.36 (29.07.2026) — Issue 6: Logo-Klick eingeloggt landet im
            passenden Cockpit (Pionier → /dashboard/pionier, sonst /cockpit).
            Ausgeloggte kommen hier normalerweise gar nicht her, aber safe: /. */}
        <Link
          to={user ? (user?.is_pionier ? "/dashboard/pionier" : "/cockpit") : "/"}
          className="flex items-center shrink-0 group"
          data-testid="dashboard-logo-link"
        >
          <img
            src="/brand/flex4success-wordmark.png"
            alt={BRAND.name}
            data-testid="dashboard-brand-wordmark"
            className="h-9 sm:h-10 w-auto select-none transition-opacity group-hover:opacity-80"
            draggable="false"
          />
        </Link>

        {/* Sprint 6 HOCH 6 AC #2 — Top-Navigation (logged-in). Mobile: hidden, ab md sichtbar. */}
        <nav className="hidden md:flex items-center gap-6 flex-1 justify-center" data-testid="dashboard-main-nav">
          {NAV_LINKS_AUTHED.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end || false}
              data-testid={link.testid}
              className={({ isActive }) =>
                `text-sm font-semibold transition-colors px-1 py-2 ${
                  isActive
                    ? "text-[var(--fts-primary-deep)]"
                    : "text-slate-600 hover:text-slate-900"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          {isDevMode && (
            <select
              data-testid="dashboard-view-as-select"
              value={override || ""}
              onChange={(e) => {
                setOverride(e.target.value || null);
                // Sprint 4 A.7 — Hard-Reload damit alle Pages den neuen
                // effektiven Fokustyp aus dem Context lesen (Daten-Refetch).
                window.location.reload();
              }}
              className="hidden sm:inline-block text-xs font-semibold rounded-md border border-[var(--fts-neutral-200)] bg-white px-2 py-1 text-[var(--fts-text-primary)]"
              title="DEV: View as Fokustyp (Override)"
            >
              <option value="">View as: (echt)</option>
              <option value="katze">View as: Katze</option>
              <option value="eule">View as: Eule</option>
              <option value="biene">View as: Biene</option>
              <option value="hund">View as: Hund</option>
            </select>
          )}
          {/* Mobile: kompakter Text-Link statt Badge */}
          <button
            type="button"
            onClick={handleBadgeClick}
            data-testid={pin.testId}
            className="sm:hidden text-xs font-semibold text-[var(--fts-primary-deep)] hover:underline"
          >
            {pin.label}
          </button>
          {/* Desktop: Badge */}
          <button
            type="button"
            onClick={handleBadgeClick}
            data-testid="dashboard-license-badge"
            className="hidden sm:inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-sm hover:opacity-90 transition-opacity"
            style={{ backgroundColor: pin.bg, color: pin.color }}
          >
            {pin.label}
          </button>

          {/* Sprint 7.1 P2 — Account-Dropdown (Avatar). Enthält:
              Mein Plan · Preise · Logout. Welcome-Label und Standalone-Logout
              entfallen (waren visuelles Duplikat). */}
          <AccountMenu
            firstName={firstName}
            onLogout={handleLogout}
            navigate={navigate}
            isPro={isProLike}
            isAdmin={user?.role === "admin"}
          />
        </div>
      </div>
      <LicenseDetailsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        licenseData={licenseData}
      />
    </header>
  );
}

// Sprint 7.1 P2 — Account-Dropdown rechts oben.
// Initial-Avatar (1 Buchstabe Vorname oder „U") → öffnet Menü mit:
//   • Mein Abo  → /dashboard/abo
//   • Preise    → /pricing
//   • Logout
function AccountMenu({ firstName, onLogout, navigate, isPro, isAdmin }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const initial = (firstName || "U").trim().charAt(0).toUpperCase() || "U";

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <div className="relative" ref={ref} data-testid="account-menu">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="account-menu-trigger"
        className="inline-flex items-center gap-1.5 rounded-full pl-1 pr-2 py-1 hover:bg-slate-100 transition-colors"
        title={firstName ? `Eingeloggt als ${firstName}` : "Account"}
      >
        <span
          aria-hidden="true"
          className="w-8 h-8 rounded-full bg-[var(--fts-primary-deep)] text-white text-sm font-extrabold flex items-center justify-center"
        >
          {initial}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          role="menu"
          data-testid="account-menu-dropdown"
          className="absolute right-0 mt-2 w-56 rounded-xl bg-white border border-slate-200 shadow-xl overflow-hidden z-50"
        >
          {firstName && (
            <div
              data-testid="account-menu-greeting"
              className="px-4 py-3 border-b border-slate-100 flex items-center gap-2.5"
            >
              <UserIcon className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-700">
                Hallo, <span className="font-semibold text-slate-900">{firstName}</span>
              </span>
            </div>
          )}
          {/* Sprint M5.40 (30.07.2026) — Block A1/B5:
              „Meine Flexpfade" und „Mein Plan" nur für Pro-User (Free hat weder
              Saved Library noch Vertragsstatus-Seite). */}
          {isPro && (
            <button
              type="button"
              role="menuitem"
              onClick={() => go("/cockpit#saved-library")}
              data-testid="account-menu-my-bridges"
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <BookOpen className="w-4 h-4 text-slate-400" />
              Meine Flexpfade
            </button>
          )}
          {isPro && (
            <button
              type="button"
              role="menuitem"
              onClick={() => go("/dashboard/abo")}
              data-testid="account-menu-abo"
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <CreditCard className="w-4 h-4 text-slate-400" />
              Mein Plan
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => go("/pricing")}
            data-testid="account-menu-pricing"
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <Tag className="w-4 h-4 text-slate-400" />
            Preise
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => go("/mitgestalten")}
            data-testid="account-menu-mitgestalten"
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 border-t border-slate-100 transition-colors"
          >
            <MessageCircle className="w-4 h-4 text-slate-400" />
            Feedback
          </button>
          {/* Sprint V101.d — Admin-Link (nur für Admins) */}
          {isAdmin && (
            <button
              type="button"
              role="menuitem"
              onClick={() => go("/admin")}
              data-testid="account-menu-admin"
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-700 hover:bg-red-50 border-t border-slate-100 transition-colors font-semibold"
            >
              <ShieldCheck className="w-4 h-4 text-red-500" />
              Admin-Cockpit
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            data-testid="account-menu-logout"
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 border-t border-slate-100 transition-colors"
          >
            <LogOut className="w-4 h-4 text-slate-400" />
            Logout
          </button>
        </div>
      )}
    </div>
  );
}
